import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Clock, Truck, UtensilsCrossed, Flame, Sparkles, Crown, BadgeCheck, Store, Zap } from 'lucide-react';
import { getOpenStatus } from '../utils/businessHours';
import { cloudinaryUrl, srcSetFor } from '../utils/cloudinaryUrl';
import FavoriteHeartButton from './FavoriteHeartButton';

// Compact, marketplace-dense restaurant card (Uber Eats / DoorDash perception, NoFee identity).
//
// Supports overlay badges via the optional `badges` prop:
//   ['trending', 'popular', 'topRated', 'new', 'freeDelivery', 'fast']
//
// Style: ~270-300px tall on desktop, 4:3 cover image, compact info block. Hover lifts + gold border.
const BADGE_META = {
  trending:     { Icon: Flame,      label: 'Trending',     cls: 'bg-rose-500/90 text-white' },
  popular:      { Icon: Sparkles,   label: 'Popular',      cls: 'bg-gold-500/95 text-black' },
  topRated:     { Icon: Crown,      label: 'Top Rated',    cls: 'bg-amber-300 text-black' },
  new:          { Icon: BadgeCheck, label: 'New',          cls: 'bg-emerald-500/95 text-black' },
  freeDelivery: { Icon: Truck,      label: 'Free Delivery',cls: 'bg-blue-500/90 text-white' },
  fast:         { Icon: Clock,      label: 'Fast',         cls: 'bg-violet-500/90 text-white' },
};

const RestaurantCard = ({ r, badges = [], size = 'md' }) => {
  const status = getOpenStatus(r.business_hours);
  const imgAspect = size === 'sm' ? 'aspect-[5/4]' : 'aspect-[4/3]';

  return (
    <Link
      to={`/menu/${r.slug}`}
      data-testid={`restaurant-card-${r.slug}`}
      className="group block rounded-xl overflow-hidden bg-zinc-900 border border-white/[0.06] hover:border-gold-500/45 hover:-translate-y-0.5 hover:shadow-[0_18px_45px_-20px_rgba(212,175,55,0.35)] transition-all"
    >
      <div className={`${imgAspect} bg-gradient-to-br from-zinc-800 to-black relative overflow-hidden`}>
        {r.cover_url ? (
          <img
            src={cloudinaryUrl(r.cover_url, { w: 480 })}
            srcSet={srcSetFor(r.cover_url, [320, 480, 720, 960]) || undefined}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            alt={r.name}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <UtensilsCrossed className="w-10 h-10 text-white/10" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />

        {/* Open/Closed dot pill (top-left, subtle) */}
        <span
          className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 text-[9.5px] font-bold rounded-full backdrop-blur ${
            status.isOpen ? 'bg-emerald-500/90 text-black' : 'bg-zinc-900/85 text-white/65 border border-white/10'
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${status.isOpen ? 'bg-black' : 'bg-rose-400'}`} />
          {status.isOpen ? 'Open' : 'Closed'}
        </span>

        {/* Featured badges + favorite heart (top-right, stacked) */}
        <div className="absolute top-2 right-2 flex flex-col items-end gap-1.5">
          <FavoriteHeartButton
            restaurantId={r.id}
            restaurantName={r.name}
            variant="overlay"
          />
          {badges.slice(0, 2).map((bk) => {
            const b = BADGE_META[bk];
            if (!b) return null;
            return (
              <span
                key={bk}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-extrabold uppercase tracking-wider ${b.cls}`}
                data-testid={`card-badge-${bk}`}
              >
                <b.Icon className="w-2.5 h-2.5" /> {b.label}
              </span>
            );
          })}
        </div>

        {/* Fast by NoFee badge is now rendered inline next to the
            restaurant name (see below) per iter134 spec. */}

        {/* Cuisine tag (bottom-left). Multi-cuisine support — show up to
            2 chips (e.g. "Italian · Pizza") plus a "+N" pill when more
            cuisines are configured. Falls back to legacy single string. */}
        {(() => {
          const list = Array.isArray(r.cuisine_types) && r.cuisine_types.length
            ? r.cuisine_types
            : (r.cuisine_type ? [r.cuisine_type] : []);
          if (!list.length) return null;
          const visible = list.slice(0, 2);
          const overflow = list.length - visible.length;
          return (
            <div className="absolute bottom-2 left-2 flex items-center gap-1">
              <span className="px-2 py-0.5 bg-black/60 backdrop-blur text-white/85 text-[10px] font-bold rounded-full">
                {visible.join(' · ')}
              </span>
              {overflow > 0 && (
                <span className="px-1.5 py-0.5 bg-black/50 backdrop-blur text-white/75 text-[10px] font-bold rounded-full">
                  +{overflow}
                </span>
              )}
            </div>
          );
        })()}
      </div>

      {/* Compact info block */}
      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-bold text-white text-[14px] truncate group-hover:text-gold-300 transition-colors">
            {r.name}
          </h3>
          {r.rating_count > 0 && (
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <Star className="w-3 h-3 fill-gold-400 text-gold-400" />
              <span className="text-[11px] font-bold text-white">{Number(r.rating_avg || 0).toFixed(1)}</span>
            </div>
          )}
        </div>

        {/* Fast by NoFee badge — rendered below the restaurant name so
            customers can quickly recognize participating restaurants
            without any tier / ranking language (iter134 spec). */}
        {r.fast_enabled && (
          <div className="mt-1.5" data-testid={`restaurant-card-${r.slug}-fast-badge`}>
            <span className="inline-flex items-center gap-1 rounded-full border border-yellow-400/45 bg-zinc-900/90 px-2 py-0.5 shadow-[0_2px_10px_-4px_rgba(212,175,55,0.45)]">
              <Zap className="w-3 h-3 text-yellow-400 fill-yellow-400" aria-hidden="true" />
              <span className="text-[10px] font-black uppercase tracking-[0.14em] text-white leading-none">Fast by NoFee</span>
            </span>
          </div>
        )}

        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-white/55">
          {/* Dynamic per-method ETA — driven by Order Fulfillment Settings
              ONLY. We no longer render the legacy `estimated_time` string
              (e.g. "30-45 min") because it was hard-coded default data
              left over from older docs and confused customers. If neither
              pickup nor delivery has an avg_minutes set on this
              restaurant, no ETA is shown at all. */}
          {(() => {
            const pickupOn = (r.pickup_enabled ?? r.accepts_pickup) && r.pickup_avg_minutes;
            const deliveryOn = (r.delivery_enabled ?? r.accepts_delivery) && r.delivery_avg_minutes;
            if (!pickupOn && !deliveryOn) return null;
            return (
              <span className="inline-flex items-center gap-1.5" data-testid={`restaurant-card-${r.slug}-eta`}>
                {pickupOn && (
                  <span className="inline-flex items-center gap-0.5" title={`Pickup ~${r.pickup_avg_minutes} min`}>
                    <Store className="w-3 h-3" />
                    {r.pickup_avg_minutes}m
                  </span>
                )}
                {pickupOn && deliveryOn && <span className="text-zinc-700">·</span>}
                {deliveryOn && (
                  <span className="inline-flex items-center gap-0.5" title={`Delivery ~${r.delivery_avg_minutes} min`}>
                    <Truck className="w-3 h-3" />
                    {r.delivery_avg_minutes}m
                  </span>
                )}
              </span>
            );
          })()}
          {r.accepts_delivery && (
            <>
              <span className="text-zinc-700">·</span>
              <span className={badges.includes('freeDelivery') ? 'text-blue-400 font-bold' : ''}>
                {r.delivery_fee > 0 ? `$${Number(r.delivery_fee).toFixed(2)}` : 'Free'}
              </span>
            </>
          )}
          {r.min_order > 0 && (
            <>
              <span className="text-zinc-700">·</span>
              <span>Min ${Number(r.min_order).toFixed(0)}</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
};

export default RestaurantCard;
